/**
 * Staff-ops request validators (plan 08 §3.1, Run 13 P1). Shared by the
 * staff-ops Edge Function and the admin-web. Clock-in/out (ST-02), commission
 * rule CRUD (ST-03), and the internal accrual event (ST-03/04, BA-04).
 */
import { z } from 'zod';

import { commission_basis, role } from '../enums.js';

/** POST /staff-ops/clock-in — set clock_in_at on an existing scheduled shift. */
export const ClockInSchema = z.object({
  staffId: z.string().uuid(),
  shiftId: z.string().uuid(),
  locationId: z.string().uuid().optional(),
  clockedAt: z.string().datetime({ offset: true }).optional(), // defaults to now() server-side
});
export type ClockInInput = z.infer<typeof ClockInSchema>;

/** POST /staff-ops/clock-out — set clock_out_at; requires a prior clock-in. */
export const ClockOutSchema = z.object({
  staffId: z.string().uuid(),
  shiftId: z.string().uuid(),
  clockedAt: z.string().datetime({ offset: true }).optional(),
});
export type ClockOutInput = z.infer<typeof ClockOutSchema>;

/**
 * Commission rule editor payload (ST-03). XOR-ish refinements:
 *  - at least one of rateBps / flatAmount,
 *  - at least one of staffId / appRole (specific staff OR a role-wide default).
 */
export const CommissionRuleSchema = z
  .object({
    staffId: z.string().uuid().optional(),
    appRole: z.enum(role).optional(),
    basis: z.enum(commission_basis),
    rateBps: z.number().int().min(1).max(10000).optional(), // 1..100% in bps
    flatAmount: z.number().int().min(1).optional(), // minor units (денари)
    isActive: z.boolean().default(true),
  })
  .refine((d) => d.rateBps != null || d.flatAmount != null, {
    message: 'commission_rule_requires_rate_or_flat',
  })
  .refine((d) => d.staffId != null || d.appRole != null, {
    message: 'commission_rule_requires_staff_or_role',
  });
export type CommissionRuleInput = z.infer<typeof CommissionRuleSchema>;

/** Internal accrual event (service-role only; never client-supplied). */
export const CommissionAccrualEventSchema = z
  .object({
    tenantId: z.string().uuid(),
    staffId: z.string().uuid(),
    basis: z.enum(commission_basis),
    sourcePaymentId: z.string().uuid().optional(),
    sourceBookingId: z.string().uuid().optional(),
    saleAmountMinorUnits: z.number().int().min(0),
  })
  .refine((d) => d.sourcePaymentId != null || d.sourceBookingId != null, {
    message: 'accrual_requires_payment_or_booking',
  });
export type CommissionAccrualEventInput = z.infer<typeof CommissionAccrualEventSchema>;
